import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaChevronUp } from "react-icons/fa";
import ZigbeeLogo from "../images/zigbee-logo-0.png";
import aw from "../images/12.png";

import { useLanguage } from "../pages/LanguageContext";
import { IoWifiOutline, IoFlashOutline, IoMicOutline } from "react-icons/io5";

const zigbeeFAQ = {
  en: [
    {
      q: "Is Zigbee still relevant??",
      a: "Absolutely. While newer protocols like Matter are designed with the future in mind, Zigbee remains one of the most stable and widely supported options for today’s smart homes.",
    },
    {
      q: "Does it need Wi-Fi?",
      a: "No. Zigbee creates its own low-power mesh network. That means less load on your router and more consistent performance for your devices.",
    },
    {
      q: "Is a hub required?",
      a: "Yes. A Zigbee hub (or gateway) connects your devices into one unified system you can control through your app or voice assistant.",
    },
  ],
  ar: [
    {
      q: "هل ما زالت زيغبي خيارًا مناسبًا؟",
      a: "عم بالتأكيد. رغم وجود بروتوكولات أحدث مثل Matter، تبقى زيغبي خيارًا موثوقًا ومدعومًا على نطاق واسع في المنازل الذكية الحالية",
    },
    {
      q: "هل تحتاج إلى شبكة واي فاي؟",
      a: "لا. زيغبي تُنشئ شبكتها الخاصة منخفضة الطاقة ولا تُحمّل الراوتر المنزلي عبئاً إضافياً",
    },
    {
      q: "هل تحتاج إلى Hub؟",
      a: "نعم. تحتاج أجهزة زيغبي إلى بوابة (Hub) لربطها معًا ضمن نظام موحد يمكن التحكم به عبر التطبيق أو المساعد الصوتي",
    },
  ],
};

const features = {
  en: [
    {
      icon: <IoWifiOutline />,
      title: "Stable Mesh Network",
      desc: "Each device extends and strengthens your network. The more devices you add, the more responsive and reliable your system becomes — especially in larger or multi-floor spaces.",
    },
    {
      icon: <IoFlashOutline />,
      title: "Built for Efficiency",
      desc: "Zigbee devices are extremely power-efficient, making them ideal for long-lasting sensors, switches, and battery-powered components.",
    },
    {
      icon: <IoMicOutline />,
      title: "Seamless Compatibility",
      desc: "Zigbee integrates easily with popular platforms like Smart Life, Alexa, and SmartThings. It powers everything from lighting and curtains to thermostats and sensors.",
    },
    {
      icon: (
        <img
          src={aw}
          alt="Zigbee Devices"
          className="zigbee-img-icon"
          style={{ width: 48, height: 48, objectFit: "contain" }}
        />
      ),

      title: "Scalable for Any Home",
      desc: "Whether you’re starting with a few lights or automating an entire villa, Zigbee’s flexibility makes it easy to grow your smart home over time.",
    },
  ],
  ar: [
    {
      icon: <IoWifiOutline />,
      title: "شبكة متماسكة ومستقرة",
      desc: "كل جهاز زيغبي يساهم في تقوية الشبكة وزيادة مدى التغطية. كلما زاد عدد الأجهزة، زادت قوة الأداء وثبات الاتصال، خاصة في المنازل الكبيرة أو متعددة الطوابق",
    },
    {
      icon: <IoFlashOutline />,
      title: "كفاءة عالية في استهلاك الطاقة",
      desc: "أجهزة زيغبي موفرة للطاقة وتعمل لفترات طويلة، مما يجعلها مثالية لأجهزة الاستشعار والمفاتيح التي تعمل بالبطارية",
    },
    {
      icon: <IoMicOutline />,
      title: "توافق واسع وسهل",
      desc: "تتوافق زيغبي بسلاسة مع أنظمة مثل Smart Life وAlexa وSmartThings، وتدعم أجهزة الإضاءة، الستائر، التكييف، الحساسات، والمزيد",
    },
    {
      icon: (
        <img
          src={aw}
          alt="Zigbee Devices"
          className="zigbee-img-icon"
          style={{ width: 48, height: 48, objectFit: "contain" }}
        />
      ),
      title: "مرنة وقابلة للتوسّع",
      desc: "سواء كنت تبدأ بعدد محدود من الأجهزة أو تخطط لأتمتة منزل كامل، تتيح لك زيغبي التوسع التدريجي بسهولة ومرونة",
    },
  ],
};

const ZigbeePage = () => {
  const [showButton, setShowButton] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    const handleScroll = () => setShowButton(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageTitle = {
    en: "Zigbee Smart Home Technology",
    ar: "تقنية المنزل الذكي زيغبي",
  };
  const pageSubtitle = {
    en: "Reliable. Responsive. Proven. Zigbee is one of the most trusted smart home protocols in the world. It’s known for its mesh networking, lightning-fast response, and solid performance in homes of all sizes.",
    ar: "موثوقة. سريعة الاستجابة. مثبتة الأداء. تُعد زيغبي (Zigbee) من أكثر بروتوكولات المنازل الذكية اعتمادًا حول العالم، وتتميز بشبكة متماسكة واستجابة فورية وأداء قوي في مختلف أنواع المنازل",
  };
  const faqTitle = {
    en: "Zigbee FAQ",
    ar: "الأسئلة الشائعة عن زيغبي",
  };

  return (
    <>
      <style>{`
        body.dark, .dark .zigbee-main-bg { background: #111 !important; color: #fff !important; }
        .dark .zigbee-header,
        .dark .zigbee-faq-card,
        .dark .zigbee-faq-title {
          background: #111 !important;
          color: #fff !important;
        }
        .dark .zigbee-header h1,
        .dark .zigbee-header h2,
        .dark .zigbee-header h3,
        .dark .zigbee-faq-title {
          color:  #1b4fbdff !important;
         
          -webkit-background-clip: unset !important;
          -webkit-text-fill-color: #1b4fbdff !important;
        }
          .zigbee-img-icon {
  transition: filter 0.3s;
}
.dark .zigbee-img-icon {
  filter: brightness(0) invert(1) grayscale(1) contrast(1.3);
}

        body.dark .zigbee-logo, .dark .zigbee-logo {
          filter: brightness(0) invert(1);
        }
        .dark .zigbee-feature-card {
          background: #000000ff !important;
          color: #fff !important;
          border: 1.5px solid #fff !important;
          
        }
        .dark .zigbee-feature-card h3 {
          color:  #1b4fbdff!important;
        }
        .dark .zigbee-feature-card p {
          color: #ffffffff !important;
        }
        .dark .zigbee-feature-card .zigbee-icon {
          color:  #ffffffff !important;
        }
        .dark .zigbee-faq-card details {
          background: #000000ff !important;
          color: #fff !important;
          border: 1.2px solid #fff !important;
        }
        .dark .zigbee-faq-card summary {
          color:  #1b4fbdff !important;
          font-weight: 700;
        }
        .dark .zigbee-faq-card p {
          color: #ffffffff !important;
        }
        .zigbee-main-bg { max-width: 100vw; overflow-x: hidden; font-family: 'Inter', 'Poppins', Arial, sans-serif;}
        .zigbee-header { text-align: center; padding: 4rem 2rem 2rem; background: #fff; }
        .zigbee-logo { width: 160px; height: auto; margin-bottom: 1rem; transition: filter 0.2s; }
        .zigbee-logo-title { font-size: 2.2rem; font-weight: 700; margin-bottom: 0.5rem; color: #1a4bb7;}
        .zigbee-logo-subtitle { font-size: 1rem; max-width: 600px; margin: 0 auto; }
        .zigbee-features { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 2.5rem; padding: 5rem 2rem; max-width: 1100px; margin: 0 auto; }
        .zigbee-feature-card { background: #fff; border-radius: 18px; padding: 2rem; box-shadow: 0 12px 24px rgba(0,0,0,0.05); text-align: center; border: 1.5px solid #e3eefd; transition: transform .17s, box-shadow .17s; }
        .zigbee-feature-card:hover { transform: translateY(-7px) scale(1.03); box-shadow: 0 16px 38px #2297e61b;}
        .zigbee-feature-card .zigbee-icon { font-size: 3.2rem;  margin-bottom: 1rem; }
        .zigbee-feature-card h3 { font-size: 1.4rem; font-weight: 700; margin-bottom: 0.6rem; color: #1a4bb7;}
        .zigbee-feature-card p { font-size: 1rem; line-height: 1.7; }
        .zigbee-faq { max-width: 800px; margin: 0 auto 5rem; padding: 0 1rem;}
        .zigbee-faq-title { text-align: center; font-size: 2rem; font-weight: 700; margin-bottom: 2rem; color: #1a4bb7;}
        .zigbee-faq-card details { background: #fff; border-radius: 12px; padding: 1rem 1.5rem; margin-bottom: 1rem; box-shadow: 0 8px 20px rgba(0,0,0,0.05); cursor: pointer; border: 1.3px solid #e3eefd;}
        .zigbee-faq-card details[open] { background: #f8fbff;}
        .zigbee-faq-card summary { font-size: 1.1rem; font-weight: 600; list-style: none; cursor: pointer;}
        .zigbee-faq-card p { font-size: 0.95rem; color: #444; margin-top: 0.6rem; line-height: 1.6;}
        .zigbee-back-btn { position: fixed; bottom: 40px; left: 25px; width: 45px; height: 45px; border-radius: 50%; background-color: #1a4bb7; color: #fff; border: none; font-size: 18px; font-weight: bold; display: flex; justify-content: center; align-items: center; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); cursor: pointer; z-index: 999; transition: opacity 0.3s, transform 0.3s;}
        .zigbee-cta { background: #f0f7ff; padding: 4rem 2rem; text-align: center; border-top: 1.5px solid #dbeafe; }
        .zigbee-cta h2 { font-size: 2rem; font-weight: 700; color: #1a4bb7; margin-bottom: 1rem; }
        .zigbee-cta p { font-size: 1rem; color: #333; max-width: 700px; margin: 0 auto 2rem; line-height: 1.7; }
        .zigbee-cta-button { display: inline-block; background: #1a4bb7; color: #fff; padding: 0.8rem 1.6rem; font-weight: 600; border-radius: 8px; text-decoration: none; transition: background 0.3s; }
        .zigbee-cta-button:hover { background: #1553b8; }
        .dark .zigbee-cta { background: #101c33; border-top: 1.5px solid #ffffffff; }
        .dark .zigbee-cta h2 { color:  #1b4fbdff; }
        .dark .zigbee-cta p { color: #ffffffff; }
        .dark .zigbee-cta-button { background:  #1b4fbdff; }
        .dark .zigbee-cta-button:hover { background: #1a6fb7; }
        @media (max-width: 700px) {
          .zigbee-features { grid-template-columns: 1fr; gap: 1.2rem; padding: 2.5rem 0.2rem; }
          .zigbee-header { padding: 2rem 0.6rem 1rem; }
        }
      `}</style>
      <main
        className="zigbee-main-bg"
        style={{
          direction: language === "ar" ? "rtl" : "ltr",
          fontFamily:
            language === "ar"
              ? "'Cairo', 'Tajawal', Tahoma, Arial, sans-serif"
              : "'Inter', 'Poppins', Arial, sans-serif",
        }}
      >
        <section className="zigbee-header" data-aos="zoom-in">
          <img src={ZigbeeLogo} alt="Zigbee Logo" className="zigbee-logo" />
          <h1 className="zigbee-logo-title">{pageTitle[language]}</h1>
          <p className="zigbee-logo-subtitle">{pageSubtitle[language]}</p>
        </section>

        <section className="zigbee-features">
          {features[language].map((feature, index) => (
            <div
              key={index}
              className="zigbee-feature-card"
              data-aos="fade-up"
              data-aos-delay={index * 200}
            >
              <div className="zigbee-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </section>

        <section className="zigbee-faq" data-aos="fade-up">
          <h2 className="zigbee-faq-title">{faqTitle[language]}</h2>
          <div className="zigbee-faq-card">
            {zigbeeFAQ[language].map((item, i) => (
              <details key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="zigbee-cta" data-aos="fade-up">
          <h2>
            {language === "ar" ? "جاهز لتجربة زيغبي؟" : "Ready for Zigbee?"}
          </h2>
          <p>
            {language === "ar"
              ? "دعنا نساعدك في بناء نظام ذكي يعتمد على زيغبي — تواصل مع مهندسينا الآن وابدأ رحلتك"
              : "Let us help you build a Zigbee-powered smart system — contact our engineers today and get started."}
          </p>
          <a href="/contact" className="zigbee-cta-button">
            {language === "ar"
              ? "👈 ابدأ مع زيغبي"
              : "👉 Start with Zigbee now"}
          </a>
        </section>

        {showButton && (
          <button onClick={scrollToTop} className="zigbee-back-btn">
            <FaChevronUp />
          </button>
        )}
      </main>
    </>
  );
};

export default ZigbeePage;
